/* WP-10 Collection detail (§14.5.3 /collect/:id): concierge status,
 * customer-facing timeline steps, items, shop card, cancel with confirm
 * through the shared step-up gate. */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { portalApi } from '../api/portal-api';
import type { PickupRequestDetail } from '../api/types';
import { Banner, FactRow, SecondaryButton } from '../components/primitives';
import { TimelineEntry } from '../components/timeline';
import { BottomSheet } from '../components/sheet';
import { useStepUp, isStepUpCancelled } from '../auth/step-up-context';
import { BackIcon, CalendarIcon, InfoIcon, LocationPinIcon, WarningIcon } from '../icons';
import { lt, shopProfile, useAppearance } from '../appearance/appearance';
import { currentPortalLocale } from '../i18n';
import { formatDate, formatDateTime } from '../utils/dates';
import { errorText } from '../utils/error-text';

export function CollectDetailPage() {
  const { t } = useTranslation();
  const locale = currentPortalLocale();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { withStepUp } = useStepUp();
  const [req, setReq] = useState<PickupRequestDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useAppearance(); // re-render when the published bundle lands/changes

  useEffect(() => {
    if (!id) return;
    portalApi
      .get<PickupRequestDetail>(`/pickup-requests/${id}`)
      .then(setReq)
      .catch((err) => setError(errorText(err, t)));
  }, [id, t]);

  const cancel = async () => {
    if (!req) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await withStepUp(() =>
        portalApi.post<PickupRequestDetail>(`/pickup-requests/${req.id}/cancel`, {}),
      );
      setReq(updated);
      setCancelOpen(false);
    } catch (err) {
      if (!isStepUpCancelled(err)) setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  if (error && !req) {
    return (
      <div className="hp-empty hp-detail-narrow">
        <p className="hp-danger-text" role="alert">
          {error}
        </p>
        <SecondaryButton onClick={() => navigate('/collect')}>{t('common.back')}</SecondaryButton>
      </div>
    );
  }
  if (!req) return <div className="hp-skel hp-detail-skel" />;

  const steps = req.timeline.steps;
  const shop = shopProfile(req.pickupShop?.id);
  const shopAddress = lt(shop?.address, locale);
  const shopHours = lt(shop?.openingHours, locale);
  const rejection = req.rejection ?? req.timeline.rejection ?? null;

  return (
    <div className="hp-detail-narrow">
      <div className="hp-row hp-detail-head">
        <button
          type="button"
          className="hp-textlink"
          onClick={() => navigate('/collect')}
          aria-label={t('common.back')}
        >
          <BackIcon size={18} />
        </button>
        <div>
          <p className="hp-label">{req.requestNumber}</p>
          <h1 className="hp-display-l">
            {t(`collect.detail.status.${req.customerStatus}`, {
              shop: req.pickupShop?.name ?? '',
            })}
          </h1>
        </div>
      </div>

      {rejection ? (
        <Banner style="notice" icon={<WarningIcon size={18} />}>
          <strong>
            {rejection.reason
              ? t(`collect.detail.rejectionReason.${rejection.reason}`, {
                  defaultValue: t('collect.detail.rejectionReason.OTHER'),
                })
              : t('collect.detail.rejectionReason.OTHER')}
          </strong>{' '}
          {rejection.messageCustomer}
        </Banner>
      ) : null}

      {error ? (
        <p className="hp-danger-text" role="alert">
          {error}
        </p>
      ) : null}

      <section className="hp-section">
        <h2 className="hp-label">{t('collect.detail.timelineTitle')}</h2>
        <div className="hp-timeline">
          {steps.map((step) => (
            <TimelineEntry
              key={step.key}
              date={step.at ?? undefined}
              dateLabel={
                step.at
                  ? formatDate(step.at, locale, { month: 'short', day: 'numeric' })
                  : undefined
              }
              ok={step.key !== 'REJECTED' && step.key !== 'CANCELLED' && step.key !== 'EXPIRED'}
            >
              {t(`collect.detail.status.${step.key}`, { shop: req.pickupShop?.name ?? '' })}
              {step.at ? (
                <span className="hp-listrow__sub">{formatDateTime(step.at, locale)}</span>
              ) : null}
            </TimelineEntry>
          ))}
        </div>
      </section>

      <section className="hp-section">
        <h2 className="hp-label">{t('collect.detail.boxes')}</h2>
        <ul className="hp-rows hp-plain-list">
          {req.lines.map((line) => (
            <li key={line.lineNo} className="hp-row hp-row--spread hp-detail-line">
              <span>
                {line.productName}
                <span className="hp-listrow__sub">
                  {line.kind === 'BOX'
                    ? `${line.brand ?? ''} · ${line.boxYear ?? '—'}${
                        line.boxRef ? ` · ${t('common.boxRef', { ref: line.boxRef })}` : ''
                      }`
                    : (line.brand ?? '')}
                </span>
              </span>
              <span className="hp-muted">{t('common.sticks', { count: line.quantitySticks })}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="hp-section">
        <div className="hp-situation__facts">
          <FactRow icon={<LocationPinIcon size={18} />}>
            {req.pickupShop?.name}
            {shopAddress ? ` — ${shopAddress}` : ''}
          </FactRow>
          <FactRow icon={<CalendarIcon size={18} />}>
            {formatDate(req.preferredPickupDate, locale)}
          </FactRow>
          {shopHours ? <FactRow icon={<InfoIcon size={18} />}>{shopHours}</FactRow> : null}
          {req.customerStatus === 'READY_FOR_PICKUP' ? (
            <FactRow icon={<InfoIcon size={18} />}>{t('collect.detail.bringId')}</FactRow>
          ) : null}
        </div>
        {req.customerNote ? <p className="hp-muted hp-quote">“{req.customerNote}”</p> : null}
      </section>

      {req.canCancel ? (
        <SecondaryButton
          danger
          onClick={() => setCancelOpen(true)}
          data-quality-id="portal.collect.cancelOpen"
        >
          {t('collect.detail.cancel')}
        </SecondaryButton>
      ) : null}

      <BottomSheet
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={t('collect.detail.cancelConfirm')}
      >
        <div className="hp-stack">
          <p>{t('collect.detail.cancelConfirm')}</p>
          <SecondaryButton danger onClick={cancel} loading={busy} data-quality-id="portal.collect.cancel">
            {t('collect.detail.cancelYes')}
          </SecondaryButton>
          <SecondaryButton onClick={() => setCancelOpen(false)}>
            {t('collect.detail.cancelKeep')}
          </SecondaryButton>
        </div>
      </BottomSheet>
    </div>
  );
}
